import { isHash, zeroAddress } from 'viem'

import { toCursorPage } from '@/helpers/cursorPage'
import { orpc } from '@/lib/orpc/client'
import { errorCodeOf } from '@/lib/orpc/errors'

import type {
  MarketBorrower,
  MarketBorrowersInput,
  MarketBorrowersOutput
} from '../borrowers.contract'

/** The procedure's input without the cursor; `marketId` is the page's route param. */
export type MarketBorrowersFilters = Omit<MarketBorrowersInput, 'cursor' | 'marketId'> & {
  marketId: string
}

/** A borrower row with the market fields every row of its page shares. */
export type MarketBorrowerItem = MarketBorrower &
  Pick<MarketBorrowersOutput, 'lltv' | 'loanAsset' | 'collateralAsset'>

/** A market the bridge does not know has no borrowers: the table shows its empty state. */
export const EMPTY_MARKET_BORROWERS: MarketBorrowersOutput = {
  items: [],
  partial: false,
  nextCursor: null,
  countTotal: 0,
  lltv: 0,
  loanAsset: { address: zeroAddress, decimals: 18, symbol: null, logoUri: null },
  collateralAsset: null
}

export const toMarketBorrowersPage = ({
  items,
  lltv,
  loanAsset,
  collateralAsset,
  ...page
}: MarketBorrowersOutput) => ({
  ...toCursorPage({
    ...page,
    items: items.map((item): MarketBorrowerItem => ({ ...item, lltv, loanAsset, collateralAsset }))
  }),
  countTotal: page.countTotal
})

// Unset filters are dropped so the server's first-page key and the hook's default key hash alike.
const definedFilters = (filters: MarketBorrowersFilters) =>
  Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== undefined))

export const marketBorrowersTableQueryKey = (filters: MarketBorrowersFilters) =>
  [...orpc.market.borrowers.key({ type: 'infinite' }), definedFilters(filters)] as const

export async function fetchMarketBorrowersPage(
  filters: MarketBorrowersFilters,
  cursor: string | undefined
) {
  const { marketId } = filters
  if (!isHash(marketId)) return toMarketBorrowersPage(EMPTY_MARKET_BORROWERS)
  try {
    return toMarketBorrowersPage(await orpc.market.borrowers.call({ ...filters, marketId, cursor }))
  } catch (error) {
    if (errorCodeOf(error) === 'NOT_FOUND') return toMarketBorrowersPage(EMPTY_MARKET_BORROWERS)
    throw error
  }
}
